<template>
  <v-app
    :class="{
      'julia-prestart-bg': !teaseStarted,
      'bm-custom-active': !!(activeBackground && activeBackground.src),
      'julia-menu-bg': isJuliaMenuPage,
      'julia-getready-bg': pageId === 'startgame',
      'julia-cbtwarning-bg': pageId === 'cbtdiff'
    }"
  >
    <!-- Background Manager: local preview and settings; online sync needs a Worker. -->
    <div v-if="activeBackground && activeBackground.src" class="bm-layer" aria-hidden="true">
      <video v-if="isBackgroundVideo(activeBackground.src)" :key="activeBackground.src + activeBackground.sound" :src="activeBackground.src" :style="backgroundCropStyle(activeBackground)" :muted="!activeBackground.sound || !bgSoundUnlocked" autoplay loop playsinline preload="auto" @click="unlockBackgroundSound"></video>
      <img v-else :src="activeBackground.src" :style="backgroundCropStyle(activeBackground)" alt="" />
    </div>
    <button v-if="backgroundManagerAllowed" type="button" class="bm-open" @click="bgManagerOpen = !bgManagerOpen">BACKGROUND MANAGER</button>
    <div v-if="bgManagerOpen && backgroundManagerAllowed" class="bm-shade" @click.self="bgManagerOpen = false">
      <section class="bm-dialog" role="dialog" aria-label="Background Manager">
        <header><strong>BACKGROUND MANAGER</strong><button type="button" @click="bgManagerOpen = false">✕</button></header>
        <div class="bm-tabs"><button v-for="name in ['intro','menu','warning']" :key="name" :class="{selected:bgEditing===name}" @click="bgEditing=name">{{ name.toUpperCase() }}</button></div>
        <div class="bm-preview">
          <video v-if="isBackgroundVideo(bgDraft.src)" :key="bgDraft.src" :src="bgDraft.src" :style="backgroundCropStyle(bgDraft)" :muted="!bgDraft.sound || !bgSoundUnlocked" autoplay loop playsinline controls></video>
          <img v-else-if="bgDraft.src" :src="bgDraft.src" :style="backgroundCropStyle(bgDraft)" alt="Background preview" />
          <span v-else>No preview selected</span>
        </div>
        <div class="bm-crop-controls">
          <strong>CROP / POSITION (JPG &amp; MP4)</strong>
          <label>ZOOM: {{ bgDraft.zoom || 100 }}%
            <input type="range" min="100" max="300" step="5" v-model.number="bgDraft.zoom" />
          </label>
          <label>HORIZONTAL: {{ bgDraft.posX == null ? 50 : bgDraft.posX }}%
            <input type="range" min="0" max="100" step="1" v-model.number="bgDraft.posX" />
          </label>
          <label>VERTICAL: {{ bgDraft.posY == null ? 50 : bgDraft.posY }}%
            <input type="range" min="0" max="100" step="1" v-model.number="bgDraft.posY" />
          </label>
          <button type="button" @click="resetBackgroundCrop">RESET CROP</button>
          <small>Non-destructive crop: adjusts the visible area without editing the original file.</small>
        </div>
        <label class="bm-field">GAME GALLERY
          <select v-model="bgDraft.src"><option value="">Choose a file...</option><option v-for="src in backgroundGallery" :key="src" :value="src">{{ src }}</option></select>
        </label>
        <label class="bm-field">OR MEDIA URL
          <input v-model.trim="bgDraft.src" placeholder="/images/ui/welcome.jpg or https://..." />
        </label>
        <label class="bm-field">UPLOAD FROM THIS DEVICE
          <input type="file" accept="image/jpeg,image/png,image/webp,video/mp4" @change="selectBackgroundFile" />
        </label>
        <label class="bm-check"><input type="checkbox" v-model="bgDraft.sound" /> MP4 SOUND ON</label>
        <p class="bm-note">Changes saved in this browser only. Online sync and mobile uploads for everyone require a Cloudflare Worker. Video audio may need a tap to start.</p>
        <div class="bm-actions"><button @click="saveBackground">SAVE</button><button @click="restoreBackground">RESTORE DEFAULT</button><button @click="unlockBackgroundSound">ENABLE SOUND</button></div>
      </section>
    </div>

    <v-main
      ref="mainPlayer"
      :class="{
        'julia-prestart-bg': !teaseStarted,
      'bm-custom-active': !!(activeBackground && activeBackground.src),
        'julia-menu-bg': isJuliaMenuPage,
        'julia-getready-bg': pageId === 'startgame',
        'julia-cbtwarning-bg': pageId === 'cbtdiff',
        'julia-video-replaces-image': !!juliaNormalVideo
      }"
    >
      <!-- Julia video player: HTML5 controls allow pause, seek, volume and fullscreen. -->
      <section v-if="activeJuliaVideo" class="julia-video-panel">
        <video :key="activeJuliaVideo.src" ref="juliaVideo" :src="activeJuliaVideo.src" :poster="activeJuliaVideo.poster" controls playsinline preload="metadata"></video>
        <p>{{ activeJuliaVideo.title }}</p>
        <small>Use RETURN TO VIDEOS or RETURN TO MENU below the player.</small>
      </section>
      <section v-else-if="pageId === 'videos'" class="julia-video-thumbnails">
        <button v-for="(item, index) in juliaVideos" :key="item.src" type="button" class="julia-video-tile" :aria-label="'Play ' + item.title" @click="openJuliaVideo(index)">
          <img :src="item.poster" :alt="item.title" />
        </button>
      </section>
      <section v-if="juliaTestVisible" class="julia-test-overlay">
        <div class="julia-test-card">
          <video v-if="isJuliaMediaVideo(juliaTestMedia)" :key="juliaTestMedia" :src="juliaTestMedia" @error="juliaMediaError" autoplay muted loop playsinline></video>
          <img v-else-if="juliaTestMedia" :src="juliaTestMedia" @error="juliaMediaError" alt="Julia's Test" />
          <h2>{{ juliaTestPhase === 'question' ? 'JULIA’S TEST' : juliaTestApproved ? 'APPROVED' : 'DISAPPROVED' }}</h2>
          <p class="julia-test-question">{{ juliaTestPhase === 'question' ? juliaTestCurrent.question : juliaTestHeading }}</p>
          <div v-if="juliaTestPhase === 'question'">
            <button v-for="(answer, i) in juliaTestCurrent.answers" :key="i" @click="answerJuliaTest(answer)">{{ answer.text }}</button>
          </div>
          <div v-else>
            <p>{{ juliaTestOutcome }}</p>
            <button v-if="juliaTestNeedsDone" type="button" @click="finishJuliaTest">DONE</button>
          </div>
        </div>
      </section>
      <section v-if="pageId === 'setvideochance'" class="julia-video-settings">
        <label for="julia-video-percent">VIDEO % — {{ juliaVideoChance }}%</label>
        <input id="julia-video-percent" type="number" min="0" max="100" step="1" :value="juliaVideoChance" @change="setJuliaVideoChance($event.target.value)" />
        <small>Normal slides only · 0–100%</small>
      </section>
      <video v-if="juliaNormalVideo" class="julia-normal-slide-video" :key="juliaNormalVideo" :src="juliaNormalVideo" autoplay muted loop playsinline></video>
      <open-eos-player
        v-if="script"
        :script="script"
        :title="title"
        :author="author"
        :author-id="authorId"
        :tease-id="teaseId"
        :tease-key="teaseKey"
        :is-fullscreen="this.isFullscreen"
        :tease-storage="teaseStorage"
        :debug-enabled="debugEnabled"
        :tease-url="teaseUrl"
        :debug-prompt="debugPrompt"
        :allow-no-sleep="allowNoSleep"
        :preview-mode="previewMode"
        :is-debug="previewMode > 0"
        @page-change="pageChange"
        @save-storage="didStorageSave"
        @load-storage="didStorageLoad"
        @tease-start="didTeaseStart"
        @tease-end="didTeaseEnd"
        @set-external-link="setExternalLink"
        @debugprompt="
          v => {
            debugPrompt = v
          }
        "
      />
      <v-container v-else>
        <loading v-if="loading">Loading MyTease...</loading>
        <v-alert v-else-if="fileError" type="error" prominent>
          {{ fileError }}
        </v-alert>
      </v-container>
    </v-main>
    <v-dialog v-model="downloadDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">
          Download Tease
        </v-card-title>

        <v-card-text>
          This will attempt to download <i>{{ displayTitle }}</i> for offline
          use. This operation could crash your browser if you don't have enough
          ram.
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="green darken-1" text @click="downloadDialog = false">
            No thanks
          </v-btn>

          <v-btn color="green darken-1" text @click="startDownload">
            Do it!
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="message.show" max-width="290">
      <v-card>
        <v-card-title class="headline">
          {{ message.title }}
        </v-card-title>

        <v-card-text v-html="message.html"> </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            v-if="message.onCancel"
            color="green darken-1"
            text
            @click="
              message.onCancel()
              closeMessage()
            "
          >
            Nope
          </v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="
              message.onContinue()
              closeMessage()
            "
          >
            Okay
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="downloading" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Downloading ({{ downloadedDisplay }})
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <input type="file" ref="teaseStorageLoader" style="display:none" />
  </v-app>
</template>

<script>
import OpenEosPlayer from './components/OpenEosPlayer'
import Loading from './components/common/Loading'
import { version } from '../package.json'
import {
  downloadObjectAsJson,
  downloadEosFile,
  convertToValidFilename,
  encodeForCorsProxy,
  corsProxyHeaders,
  FIX_POLLUTION,
  getFormattedDateForFile,
  acronym,
} from './util/io'
import prettysize from 'prettysize'
import CryptoJS from 'crypto-js'

// Public Cloudflare R2 location for MP4 files (photos remain on Pages).
const JULIA_R2_VIDEO_BASE = 'https://pub-ddd6485e53a9492c9e4a3e8526349052.r2.dev/JULIA_VIDEOS_UPLOAD/'

function juliaR2VideoUrl(value) {
  if (typeof value !== 'string' || !/\.mp4(?:[?#]|$)/i.test(value)) return value
  if (/^(?:https?:|blob:|data:)/i.test(value)) return value
  const clean = value.replace(/^\/?(?:public\/)?images\//i, '').replace(/^\/?JULIA_VIDEOS_UPLOAD\//i, '').replace(/^\/+/, '')
  if (!/^(?:feet|socks|julia-test)\//i.test(clean)) return value
  const parts = clean.split('/')
  return JULIA_R2_VIDEO_BASE + parts.map(part => encodeURIComponent(decodeURIComponent(part))).join('/')
}

function juliaMapR2VideoPaths(value) {
  if (typeof value === 'string') return juliaR2VideoUrl(value)
  if (Array.isArray(value)) return value.map(juliaMapR2VideoPaths)
  if (value && typeof value === 'object') {
    Object.keys(value).forEach(key => { value[key] = juliaMapR2VideoPaths(value[key]) })
  }
  return value
}


const parser = new DOMParser()

export default {
  name: 'App',
  components: {
    OpenEosPlayer,
    Loading,
  },
  computed: {
    backgroundManagerAllowed() {
      return !this.teaseStarted || this.pageId === 'menu' || this.pageId === 'startgame'
    },
    activeBackground() {
      const name = !this.teaseStarted ? 'intro' : this.pageId === 'startgame' ? 'warning' : this.isJuliaMenuPage ? 'menu' : null
      return name ? { ...this.bgSettings[name], src: juliaR2VideoUrl(this.bgSettings[name].src) } : null
    },
    bgDraft() { return this.bgSettings[this.bgEditing] },
    backgroundGallery() {
      const found = new Set(['/images/ui/welcome.jpg', '/images/ui/menu.jpg', '/images/ui/getready.jpg'])
      const scan = (value, depth = 0) => {
        if (depth > 6 || value == null) return
        if (typeof value === 'string') {
          if (/\.(?:jpe?g|png|webp|mp4)(?:[?#].*)?$/i.test(value)) found.add(value)
        } else if (Array.isArray(value)) value.forEach(v => scan(v, depth + 1))
        else if (typeof value === 'object') Object.values(value).forEach(v => scan(v, depth + 1))
      }
      if (this.script) { scan(this.script.galleries); scan(this.script.files) }
      if (this.juliaTestConfig) scan(this.juliaTestConfig)
      return [...found].map(juliaR2VideoUrl).sort()
    },
    downloadedDisplay() {
      return prettysize(this.downloaded)
    },
    errors() {
      return this.error ? [this.error] : []
    },
    fileErrors() {
      return this.fileError ? [this.fileError] : []
    },
    displayTitle() {
      return this.title ? this.title : 'this tease'
    },
    debugEnabled() {
      return !this.formUri || !!this.previewMode
    },
    juliaVideos() {
      return [
        { title: 'Dirty Feet JOI + Countdown', src: 'https://pub-ddd6485e53a9492c9e4a3e8526349052.r2.dev/video1.mp4', poster: '/videos/video1.jpg' },
        { title: "Julia's Training", src: 'https://pub-ddd6485e53a9492c9e4a3e8526349052.r2.dev/video2.mp4', poster: '/videos/video2.jpg' },
        { title: 'Feet SPH', src: 'https://pub-ddd6485e53a9492c9e4a3e8526349052.r2.dev/video3.mp4', poster: '/videos/video3.jpg' },
      ]
    },
    activeJuliaVideo() {
      const index = ['video1', 'video2', 'video3'].indexOf(this.pageId)
      return index < 0 ? null : this.juliaVideos[index]
    },
    isJuliaMenuPage() {
      return [
        'menu',
        'pictures',
        'setfeet',
        'setworship',
        'setvideochance',
        'lights',
        'setgreen',
        'cbtmenu',
        'cbtdiff',
        'slapmin',
        'slapmax',
        'strokes',
        'setone',
        'settwo',
        'juliatestmenu', 'juliatestfrequency', 'juliatestmood', 'juliatestmin', 'juliatestmax',
        'videos',
        'video1',
        'video2',
        'video3',
      ].includes(this.pageId)
    },
  },
  data: () => ({
    bgManagerOpen: false,
    bgEditing: 'intro',
    bgSoundUnlocked: false,
    bgSettings: {
      intro: { src: '/images/ui/welcome.jpg', sound: true, zoom: 100, posX: 50, posY: 50 },
      menu: { src: '/images/ui/menu.jpg', sound: false, zoom: 100, posX: 50, posY: 50 },
      warning: { src: '', sound: true, zoom: 100, posX: 50, posY: 50 },
    },
    juliaTestConfig: null,
    juliaTestVisible: false,
    juliaTestCurrent: null,
    juliaTestMedia: null,
    juliaTestPhase: 'question',
    juliaTestApproved: false,
    juliaTestHeading: '',
    juliaTestOutcome: '',
    juliaTestLastId: null,
    juliaTestMin: 5,
    juliaTestTimeout: null,
    juliaTestNeedsDone: false,
    juliaTestMediaFallbacks: [],
    juliaTestMax: 15,
    juliaTestMood: 'unpredictable',
    juliaNormalVideo: null,
    juliaVideoChance: 30,
    isFullscreen: false,
    script: null,
    eosuri: null,
    error: null,
    fileError: null,
    title: null,
    author: null,
    authorId: null,
    teaseId: null,
    teaseKey: null,
    teaseUrl: null,
    formUri: false,
    previewMode: 0,
    loading: false,
    version: version,
    downloadDialog: false,
    downloading: false,
    fileUpload: null,
    externalLink: null,
    pageId: null,
    downloaded: 0,
    hasStorage: false,
    teaseStarted: false,
    teaseStorage: null,
    debugPrompt: false,
    allowNoSleep: false,
    message: {
      title: null,
      html: null,
      show: false,
    },
  }),
  mounted() {
    try {
      const stored = JSON.parse(localStorage.getItem('julia-backgrounds-v1') || '{}')
      for (const name of ['intro', 'menu', 'warning']) {
        if (stored[name] && typeof stored[name].src === 'string' && !stored[name].src.startsWith('blob:')) {
          this.$set(this.bgSettings, name, { src: juliaR2VideoUrl(stored[name].src), sound: !!stored[name].sound, zoom: Math.min(300, Math.max(100, Number(stored[name].zoom) || 100)), posX: Math.min(100, Math.max(0, Number(stored[name].posX == null ? 50 : stored[name].posX) || 0)), posY: Math.min(100, Math.max(0, Number(stored[name].posY == null ? 50 : stored[name].posY) || 0)) })
        }
      }
    } catch (e) { console.warn('Background settings could not be loaded', e) }
    // Hide the OEOS visual countdown only. Timers still run normally.
    this.$nextTick(() => {
      this._juliaTimerObserver = new MutationObserver(() => this.hideVisibleTimers())
      this._juliaTimerObserver.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      })
      this.hideVisibleTimers()
    })

    fetch(`/julia-test.json?v=${Date.now()}`, { cache: 'no-store' })
      .then(r => { if (!r.ok) throw new Error('Missing julia-test.json'); return r.json() })
      .then(data => { this.juliaTestConfig = juliaMapR2VideoPaths(data) })
      .catch(err => console.warn('Julia Test disabled:', err))
    const params = new URLSearchParams(window.location.search)
    const teaseId = params.get('id')

    if (teaseId) {
      this.formUri = true
      let teaseUrl = `https://milovana.com/webteases/showtease.php?id=${teaseId}`
      const key = params.get('key')
      if (key) teaseUrl += `&key=${key}`
      this.teaseUrl = teaseUrl
      this.loadMilovanaUrl()
    } else {
      this.loading = true
      this.fileError = null
      fetch(`/tease.json?v=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      })
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          return response.json()
        })
        .then(script => {
          if (!script || !script.pages) throw new Error('Invalid tease.json')
          const meta = script.oeosmeta || {}
          this.title = meta.title || "Goddess Julia's Humiliaton Tease"
          this.author = meta.author || ""
          this.authorId = meta.authorId || null
          this.teaseId = meta.teaseId || 'mytease-private'
          this.teaseKey = meta.teaseKey || null
          this.script = juliaMapR2VideoPaths(script)
        })
        .catch(error => {
          console.error('AUTOLOAD failed:', error)
          this.fileError = `Could not auto-load /tease.json: ${error.message}`
        })
        .finally(() => { this.loading = false })
    }
  },


  beforeDestroy() {
    if (this._juliaTimerObserver) this._juliaTimerObserver.disconnect()
    clearTimeout(this.juliaTestTimeout)
  },

  methods: {
    backgroundCropStyle(settings) {
      const zoom = Math.min(300, Math.max(100, Number(settings.zoom) || 100))
      const x = Math.min(100, Math.max(0, Number(settings.posX == null ? 50 : settings.posX)))
      const y = Math.min(100, Math.max(0, Number(settings.posY == null ? 50 : settings.posY)))
      return { objectFit: 'cover', objectPosition: `${x}% ${y}%`, transform: `scale(${zoom / 100})`, transformOrigin: `${x}% ${y}%` }
    },
    resetBackgroundCrop() {
      this.$set(this.bgDraft, 'zoom', 100)
      this.$set(this.bgDraft, 'posX', 50)
      this.$set(this.bgDraft, 'posY', 50)
    },
    isBackgroundVideo(src) { return /\.mp4(?:[?#]|$)/i.test(src || '') || (src || '').startsWith('blob:') && this.bgUploadedIsVideo },
    unlockBackgroundSound() {
      this.bgSoundUnlocked = true
      this.$nextTick(() => document.querySelectorAll('.bm-layer video').forEach(v => v.play().catch(() => {})))
    },
    selectBackgroundFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (!['image/jpeg', 'image/png', 'image/webp', 'video/mp4'].includes(file.type)) { alert('Choose JPG, PNG, WEBP or MP4'); return }
      if (this.bgTempUrl) URL.revokeObjectURL(this.bgTempUrl)
      this.bgTempUrl = URL.createObjectURL(file)
      this.bgUploadedIsVideo = file.type === 'video/mp4'
      this.bgDraft.src = this.bgTempUrl
      alert('Local preview only. To make uploaded media permanent on all devices, it must first be uploaded to Cloudflare R2 and selected using its public URL.')
    },
    saveBackground() {
      if ((this.bgDraft.src || '').startsWith('blob:')) { alert('This uploaded file is a temporary preview. Upload it to R2 and paste its public URL to save permanently.'); return }
      this.bgDraft.src = juliaR2VideoUrl(this.bgDraft.src)
      localStorage.setItem('julia-backgrounds-v1', JSON.stringify(this.bgSettings))
      this.bgManagerOpen = false
    },
    restoreBackground() {
      const defaults = { intro: { src: '/images/ui/welcome.jpg', sound: true, zoom: 100, posX: 50, posY: 50 }, menu: { src: '/images/ui/menu.jpg', sound: false, zoom: 100, posX: 50, posY: 50 }, warning: { src: '', sound: true } }
      this.$set(this.bgSettings, this.bgEditing, { ...defaults[this.bgEditing] })
      localStorage.setItem('julia-backgrounds-v1', JSON.stringify(this.bgSettings))
    },
    setJuliaVideoChance(value) {
      const n = Number(value)
      if (Number.isFinite(n)) this.juliaVideoChance = Math.max(0, Math.min(100, Math.round(n)))
    },
    isJuliaMediaVideo(src) { return /\.mp4(?:\?|$)/i.test(src || '') },
    juliaRandom(items) { return items && items.length ? items[Math.floor(Math.random() * items.length)] : null },
    juliaMediaError() {
      // A missing MP4/JPG must never show a broken-image icon or block the test.
      this.juliaTestMedia = this.juliaTestMediaFallbacks.shift() || null
    },
    juliaSetMedia(source) {
      // Use only the selected question's own media; never substitute unrelated clips.
      const candidates = (Array.isArray(source) ? source : []).filter(Boolean)
      const picked = this.juliaRandom(candidates)
      const declared = [picked, ...candidates.filter(item => item !== picked)].filter(Boolean)
      const expanded = []
      declared.forEach(path => {
        if (!expanded.includes(path)) expanded.push(path)
        const match = path.match(/\.(mp4|jpg|jpeg|png|webp)$/i)
        if (match) ['.mp4', '.jpg', '.jpeg', '.png', '.webp'].forEach(ext => {
          const alternate = path.slice(0, -match[0].length) + ext
          if (!expanded.includes(alternate)) expanded.push(alternate)
        })
      })
      this.juliaTestMedia = juliaR2VideoUrl(expanded.shift() || null)
      this.juliaTestMediaFallbacks = expanded.map(juliaR2VideoUrl)
      if (!this.juliaTestMedia && candidates.length) console.warn('No media candidates for Julia question')
    },
    juliaResultDuration(outcome) {
      const label = (outcome || '').toUpperCase()
      let min = 6, max = 15
      if (label.includes('TWO FINGER')) { min = 6; max = 12 }
      else if (label.includes('ONE FINGER')) { min = 5; max = 9 }
      else if (label.includes('NORMAL')) { min = 6; max = 9 }
      return (min + Math.floor(Math.random() * (max - min + 1))) * 1000
    },
    startJuliaTest() {
      const cfg = this.juliaTestConfig
      if (!cfg || !cfg.questions || !cfg.questions.length) { this.finishJuliaTest(); return }
      const choices = cfg.questions.filter(q => q.id !== this.juliaTestLastId)
      const q = this.juliaRandom(choices.length ? choices : cfg.questions)
      this.juliaTestCurrent = q
      this.juliaTestLastId = q.id
      this.juliaTestPhase = 'question'
      this.juliaTestNeedsDone = false
      clearTimeout(this.juliaTestTimeout)
      this.juliaSetMedia(q.questionImages && q.questionImages.length ? q.questionImages : cfg.media.question)
      this.juliaTestVisible = true
    },
    answerJuliaTest(answer) {
      const moodAdjustment = { forgiving: 15, unpredictable: 0, strict: -15 }[this.juliaTestMood] || 0
      const chance = Math.max(0, Math.min(100, Number(answer.approvalChance) + moodAdjustment))
      this.juliaTestApproved = Math.random() * 100 < chance
      const reactions = this.juliaTestCurrent.reactions || {}
      this.juliaTestHeading = this.juliaRandom(this.juliaTestApproved ? reactions.approvalHeadings : reactions.disapprovalHeadings) || ''
      let outcome = ''
      let isExtraPunishment = false
      if (this.juliaTestApproved) {
        // Weighted rewards: NORMAL 69%, TWO FINGER 20%, ONE FINGER 11%.
        const rewards = reactions.rewards || []
        const normal = rewards.find(r => /NORMAL STROKES/i.test(r))
        const two = rewards.find(r => /TWO FINGER STROKES/i.test(r))
        const one = rewards.find(r => /ONE FINGER STROKES/i.test(r))
        const roll = Math.random() * 100
        outcome = (roll < 69 ? normal : roll < 89 ? two : one) || this.juliaRandom(rewards) || ''
      } else {
        const punishments = reactions.punishments || []
        const extra = punishments.find(p => /really really hard and squirm for me loser/i.test(p))
        // Exactly 4% of disapproved results select the third punishment.
        isExtraPunishment = Boolean(extra) && Math.random() * 100 < 4
        const regular = punishments.filter(p => p !== extra)
        outcome = isExtraPunishment ? extra : (this.juliaRandom(regular) || '')
      }
      const count = isExtraPunishment
        ? 3 + Math.floor(Math.random() * 3)
        : Math.floor(Math.random() * (this.juliaTestMax - this.juliaTestMin + 1)) + this.juliaTestMin
      this.juliaTestOutcome = outcome.replace(/"x"/g, String(count))
      this.juliaSetMedia(this.juliaTestApproved ? this.juliaTestConfig.media.approval : this.juliaTestConfig.media.disapproval)
      this.juliaTestPhase = 'result'
      clearTimeout(this.juliaTestTimeout)
      this.juliaTestNeedsDone = isExtraPunishment || (!this.juliaTestApproved && /PUNISHMENT:\s*Slap\b/i.test(outcome))
      if (!this.juliaTestNeedsDone) {
        this.juliaTestTimeout = setTimeout(() => this.finishJuliaTest(), this.juliaResultDuration(outcome))
      }
    },
    finishJuliaTest() {
      clearTimeout(this.juliaTestTimeout)
      this.juliaTestTimeout = null
      this.juliaTestVisible = false
      this.juliaTestNeedsDone = false
      this.juliaTestMedia = null
      this.$nextTick(() => {
        const root = this.$refs.mainPlayer && this.$refs.mainPlayer.$el
        const buttons = root ? Array.from(root.querySelectorAll('button')) : []
        const next = buttons.find(b => b.textContent && b.textContent.trim() === 'CONTINUE')
        if (next) next.click()
      })
    },
    updateNormalVideo(pageId) {
      this.juliaNormalVideo = null
      // The 'next' and 'next_after_test' pages are only routing pages.
      // Choose media when a visible gameplay slide begins, not on the router.
      const slidePages = ['green', 'one', 'two', 'red', 'slap']
      if (!slidePages.includes(pageId) || !this.juliaTestConfig || !this.juliaTestConfig.normalSlideVideos) return
      if (Math.random() * 100 >= this.juliaVideoChance) return
      const type = Math.random() < 0.8 ? 'feet' : 'socks'
      const sources = this.juliaTestConfig.normalSlideVideos
      this.juliaNormalVideo = juliaR2VideoUrl(this.juliaRandom(sources[type]) || this.juliaRandom(sources.feet) || this.juliaRandom(sources.socks))
    },
    hideVisibleTimers() {
      // Hide ONLY the visual countdown. EOS timers keep running normally.
      const nodes = document.querySelectorAll('body *')
      nodes.forEach(el => {
        if (!el || el.children.length) return
        const txt = (el.textContent || '').trim()
        const looksLikeTimer =
          /^\d{1,3}:\d{2}$/.test(txt) ||
          /^\d{1,2}:\d{2}:\d{2}$/.test(txt) ||
          /^\d{1,3}\s*s$/i.test(txt)
        if (!looksLikeTimer) return
        const r = el.getBoundingClientRect()
        if (
          r.width > 0 &&
          r.height > 0 &&
          r.top < 220
        ) {
          el.style.setProperty('display', 'none', 'important')
          if (el.parentElement && el.parentElement.children.length === 1) {
            el.parentElement.style.setProperty('display', 'none', 'important')
          }
        }
      })
    },
    setExternalLink(link) {
      this.externalLink = link || null
    },
    openExternalLink() {
      if (!this.externalLink || !this.externalLink.link) return
      window.open(this.externalLink.link, '_blank').focus()
    },
    restoreTeaseStorage() {
      const input = this.$refs.teaseStorageLoader
      input.type = 'file'
      input.accept = `.oeos_${this.teaseId}`
      input.addEventListener('change', handleFiles, false)
      const vue = this
      function handleFiles() {
        const file = this.files[0]
        if (file) {
          var reader = new FileReader()
          reader.onload = function(e) {
            try {
              const bytes = CryptoJS.AES.decrypt(
                JSON.parse(e.target.result),
                vue.teaseId + '::OEOS'
              )
              const storageData = bytes.toString(CryptoJS.enc.Utf8)
              if (
                storageData &&
                storageData.startsWith('{') &&
                JSON.parse(storageData)
              ) {
                vue.teaseStorage = storageData
              } else {
                console.error('Invalid data?', bytes, storageData)
                throw new Error('Invalid tease storage file', storageData)
              }
            } catch (err) {
              vue.showMessage(
                'Invalid Tease Storage',
                'Sorry, that file does not appear to be a valid storage file for this tease.'
              )
              console.error(err)
            }
          }
          reader.readAsText(file)
        }
      }
      input.click()
    },
    downloadTeaseStorage() {
      const storageData = CryptoJS.AES.encrypt(
        this.teaseStorage,
        this.teaseId + '::OEOS'
      ).toString()
      downloadObjectAsJson(
        storageData,
        convertToValidFilename(
          acronym(this.title) + '-' + getFormattedDateForFile()
        ),
        `oeos_${this.teaseId}`
      )
    },
    didStorageSave(v) {
      if (v && v !== '{}') {
        this.hasStorage = true
      }
      if (v) this.teaseStorage = v
    },
    didStorageLoad(v) {
      if (v && v !== '{}') {
        this.hasStorage = true
      } else if (!v) {
        this.hasStorage = false
      } else {
        this.hasStorage = null
      }
      console.log('Did storage load', v)
      if (v) this.teaseStorage = v
    },
    didTeaseStart() {
      this.teaseStarted = true
    },
    didTeaseEnd() {},
    openJuliaVideo(index) {
      // Use the existing EOS choice so its internal page state stays synchronized.
      const labels = ['Dirty Feet JOI', 'Julia', 'Feet SPH']
      const root = this.$refs.mainPlayer && this.$refs.mainPlayer.$el
      const buttons = root ? Array.from(root.querySelectorAll('button, [role="button"]')) : []
      const match = buttons.find(el => el.textContent && el.textContent.includes(labels[index]) && !el.classList.contains('julia-video-tile'))
      if (match) match.click()
      else console.warn('EOS video choice not found:', labels[index])
    },
    pageChange(pageId) {
      if (this.$refs.juliaVideo) {
        this.$refs.juliaVideo.pause()
        try { this.$refs.juliaVideo.currentTime = 0 } catch (e) { /* metadata not loaded yet */ }
      }
      this.pageId = pageId
      if (!this.backgroundManagerAllowed) this.bgManagerOpen = false
      if (pageId === 'juliatest') this.startJuliaTest()
      else if (this.juliaTestVisible) {
        clearTimeout(this.juliaTestTimeout)
        this.juliaTestVisible = false
      }
      this.updateNormalVideo(pageId)
      this.$nextTick(() => this.hideVisibleTimers())
    },
    closeMessage() {
      this.message.show = false
      this.message.onContinue = () => {}
      this.message.onCancel = false
    },
    showMessage(title, html, onContinue, onCancel) {
      this.message.title = title
      this.message.html = html
      this.message.onContinue = onContinue || function() {}
      this.message.onCancel = onCancel
      this.message.show = true
    },
    async uploadFile() {
      console.log('Ready to upload', this.fileUpload)
      try {
        const script = JSON.parse(await this.fileUpload.text())
        if (
          !script ||
          !script.pages ||
          (script.modules && script.modules.nyx)
        ) {
          if (script.modules && script.modules.nyx) {
            this.fileError = 'Sorry, NYX teases are not supported.'
          } else {
            this.fileError =
              'Does not appear to be a valid EOS tease (Invalid Definitions)'
          }
        } else {
          if (script.oeosmeta) {
            const meta = script.oeosmeta
            this.title = meta.title
            this.author = meta.author
            this.authorId = meta.authorId
            this.teaseId = meta.teaseId
            this.teaseKey = meta.teaseKey
            this.script = script
          } else {
            this.message.title = 'Warning'
            this.message.html = `This appears to be a raw EOS file, not an Open EOS file.<br>
            Images, etc., will still be loaded from Milovana, and I won't know the title or author of this tease.<br>
            <br>
            Do you want to continue?`
            this.message.onContinue = () => {
              this.title = this.fileUpload.name
              this.script = script
              this.author = 'Unknown'
            }
            this.message.onCancel = () => {}
            this.message.show = true
          }
        }
      } catch (e) {
        this.fileError = 'Invalid JSON ffile'
      }
    },
    async downloadTease() {
      this.downloaded = 0
      const script = this.script
      if (script.galleries) {
        for (const gallery of Object.values(script.galleries)) {
          console.log('Downloading gallery', gallery)
          for (const image of gallery.images) {
            console.log('Downloading gallery image', image)
            this.downloaded += await downloadEosFile(image, true)
          }
        }
      }
      if (script.files) {
        for (const file of Object.values(script.files)) {
          console.log('Downloading file', file)
          this.downloaded += await downloadEosFile(file, true)
        }
      }
      script.oeosmeta = {
        title: this.title,
        author: this.author,
        authorId: this.authorId,
        teaseId: this.teaseId,
        teaseKey: this.teaseKey,
      }
      downloadObjectAsJson(
        script,
        convertToValidFilename(this.title || 'unnamed_tease') + '.oeos.json'
      )
    },
    startDownload() {
      this.downloadDialog = false
      this.downloading = true
      this.downloadTease()
        .then(() => {
          this.downloading = false
        })
        .catch(() => {
          this.downloading = false
          this.message.title = 'Error'
          this.message.html = 'Unable to download tease for unknown reason.'
          this.message.show = true
        })
    },
    loadMilovanaUrl() {
      this.error = null
      this.hasStorage = false
      this.easeStarted = false
      if (this.loading) return
      const uri = this.parseTeaseURI()
      if (!uri) {
        this.error = 'Invalid tease URL'
      } else {
        // this.getRemoteScriptName(uri)
        this.getRemoteScript(uri)
      }
    },
    toggleFullscreen() {
      this.isFullscreen = true
      requestAnimationFrame(() => this.$refs.mainPlayer.$el.requestFullscreen())
    },
    parseTeaseURI() {
      var teaseId = (this.teaseUrl || '').match(/id=([0-9a-z]+.*)/i)
      return (teaseId && teaseId[1]) || ''
    },
    getRemoteScript(uri) {
      this.loading = true
      fetch(
        encodeForCorsProxy(
          'https://milovana.com/webteases/geteosscript.php',
          `id=${uri}&${FIX_POLLUTION}` +
            (this.previewMode
              ? '&ncpreview=' + this.previewMode
              : '&cacheable&_nc=' + Math.floor(Date.now() / 1000 / 60))
        ),
        { headers: corsProxyHeaders() }
      )
        .then(response => response.json())
        .then(script => {
          if (
            !script ||
            !script.pages ||
            (script.modules && script.modules.nyx)
          ) {
            if (script.modules && script.modules.nyx) {
              this.error = 'Sorry, NYX teases are not supported.'
            } else {
              this.error =
                'Does not appear to be a valid EOS tease (Invalid Definitions)'
            }

            this.loading = false
          } else {
            this.getRemoteScriptName(uri, script)
          }
        })
        .catch(e => {
          fetch(
            encodeForCorsProxy(
              `https://milovana.com/webteases/showtease.php`,
              `&id=${uri}&${FIX_POLLUTION}` +
                (this.previewMode
                  ? '&preview=' + this.previewMode
                  : '&cacheable&_nc=' + Math.floor(Date.now() / 1000 / 60))
            ),
            { headers: corsProxyHeaders() }
          )
            .then(response => response.text())
            .then(contents => {
              console.log('Looking for old-school tease', contents)
              this.loading = false
              if (
                parser
                  .parseFromString(contents, 'text/html')
                  .getElementById('tease_title')
              ) {
                this.error = 'Sorry, classic teases are not supported.'
              } else {
                throw e
              }
            })
            .catch(() => {
              this.error =
                'Does not appear to be a valid EOS tease (Invalid JSON)'
              console.error('JSON response error', e)
              this.loading = false
            })
        })
    },
    getRemoteScriptName(uri, script) {
      fetch(
        encodeForCorsProxy(
          `https://milovana.com/webteases/showtease.php`,
          `id=${uri}&${FIX_POLLUTION}` +
            (this.previewMode
              ? '&ncpreview=' + this.previewMode
              : '&cacheable&_nc=' + Math.floor(Date.now() / 1000 / 60))
        ),
        { headers: corsProxyHeaders() }
      )
        .then(response => response.text())
        .then(contents => {
          this.loading = false
          // console.log('HTML response', contents)
          try {
            const doc = parser
              .parseFromString(contents, 'text/html')
              .getElementsByTagName('body')[0]
            this.title = doc.getAttribute('data-title')
            this.author = doc.getAttribute('data-author')
            this.authorId = doc.getAttribute('data-author-id')
            this.teaseId = doc.getAttribute('data-tease-id')
            this.teaseKey = doc.getAttribute('data-key')
            if (this.title) document.title = this.title
            this.script = script
          } catch (e) {
            console.error('Error parseing EOS HTML', e)
            this.title = 'Error Getting Title'
          }
        })
        .catch(e => {
          this.error = 'Error loading tease HTML from Milovana'
          console.error('HTML response error', e)
          this.loading = false
        })
    },
  },
}
</script>
<style>
.oeos-title b {
  display: inline-block;
  margin-right: 5px;
}
.oeos-title span {
  opacity: 0.8;
}
.oeos-title svg {
  margin-bottom: -5px;
}
.oeos-title svg + b {
  margin-left: 5px;
}
.theme--dark.v-application {
  background-color: var(--v-background-base, #121212) !important;
}
.theme--light.v-application {
  background-color: var(--v-background-base, white) !important;
}

/* JULIA custom start/menu backgrounds.
   cover = fills the viewport without stretching; edges may crop naturally. */
.julia-prestart-bg,
.julia-menu-bg {
  background-color: #000 !important;
  background-repeat: no-repeat !important;
  background-position: center center !important;
  background-size: cover !important;
  background-attachment: fixed !important;
}

.julia-prestart-bg {
  background-image: url('/images/ui/welcome.jpg') !important;
}

.julia-menu-bg {
  background-image: url('/images/ui/menu.jpg') !important;
}

/* Let the background show through the player on these screens. */
.julia-prestart-bg .v-application--wrap,
.julia-prestart-bg .v-main,
.julia-prestart-bg .v-main__wrap,
.julia-menu-bg .v-application--wrap,
.julia-menu-bg .v-main,
.julia-menu-bg .v-main__wrap {
  background-color: transparent !important;
}

/* The menu must use the whole viewport; no reserved red toolbar strip. */
.julia-menu-bg .v-main,
.julia-prestart-bg .v-main {
  padding-top: 0 !important;
  min-height: 100vh !important;
}

/* Extra timer selectors for OEOS/Vuetify builds where a timer class is exposed. */
.oeos-timer,
.eos-timer,
.timer-display,
.countdown-timer,
[class*="timer-display"],
[class*="countdown-timer"] {
  display: none !important;
}


/* FINAL JULIA MENU FIX:
   menu.jpg fills the entire viewport, including the old toolbar area.
   cover preserves aspect ratio (no stretching), cropping edges when needed. */
.julia-menu-bg {
  background-image: url('/images/ui/menu.jpg') !important;
  background-size: cover !important;
  background-position: center center !important;
  background-repeat: no-repeat !important;
  min-height: 100vh !important;
}
.julia-menu-bg .v-app-bar {
  display: none !important;
}
.julia-menu-bg .v-main {
  padding: 0 !important;
  min-height: 100vh !important;
}
.julia-menu-bg .v-main__wrap,
.julia-menu-bg open-eos-player,
.julia-menu-bg open-eos-player > *,
.julia-menu-bg [class*="player"],
.julia-menu-bg [class*="page"] {
  background-color: transparent !important;
}

/* Welcome image is the background of the ORIGINAL MyTease / by Private screen. */
.julia-prestart-bg {
  background-image: url('/images/ui/welcome.jpg') !important;
  background-size: cover !important;
  background-position: center center !important;
  background-repeat: no-repeat !important;
  min-height: 100vh !important;
}

/* Known timer/countdown class variants. */
.oeos-timer, .eos-timer, .timer-display, .countdown-timer,
[class*="timer-display"], [class*="countdown-timer"],
[class*="timerDisplay"], [class*="countdownTimer"] {
  display: none !important;
}


/* ===== JULIA FINAL VISUAL OVERRIDES ===== */

/* Focal points based on the blue dots supplied by the user.
   cover keeps aspect ratio; it does NOT stretch the image. */
.julia-menu-bg {
  background-position: 40% 65% !important;
}
.julia-prestart-bg {
  background-position: 35% 76% !important;
}

/* Menu text/control area: move it upward toward the centre.
   Target the central EOS overlay without moving the full-screen background. */
.julia-menu-bg open-eos-player .v-overlay__content,
.julia-menu-bg open-eos-player [class*="notification"],
.julia-menu-bg open-eos-player [class*="controls"] {
  transform: translateY(-85px) !important;
}

/* Replace solid black menu cards/panels with translucent frosted glass.
   Keep text crisp; backdrop-filter blurs only what is behind the panel. */
.julia-menu-bg open-eos-player .v-card,
.julia-menu-bg open-eos-player .v-sheet,
.julia-menu-bg open-eos-player [style*="background: black"],
.julia-menu-bg open-eos-player [style*="background:#000"],
.julia-menu-bg open-eos-player [style*="background: #000"] {
  background: rgba(10, 10, 10, 0.48) !important;
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
}

/* Timer visual only: timing logic continues to run. */
.oeos-timer, .eos-timer, .timer-display, .countdown-timer,
[class*="timer-display"], [class*="countdown-timer"],
[class*="timerDisplay"], [class*="countdownTimer"],
[class*="timer"][class*="display"],
[class*="countdown"] {
  display: none !important;
}


/* ===== JULIA MENU / INTRO FINAL TUNING ===== */

/* INTRO: anchor the portrait image to the bottom so the shoes/lower legs
   are visible, matching the reference crop. No stretching: cover preserves ratio. */
.julia-prestart-bg {
  background-position: 50% 100% !important;
}

/* MENU background focal point */
.julia-menu-bg {
  background-position: 40% 65% !important;
}

/* Move the entire menu UI upward toward screen centre. */
.julia-menu-bg open-eos-player {
  position: relative !important;
  top: -95px !important;
}

/* Frosted-glass menu panels */
.julia-menu-bg open-eos-player .v-card,
.julia-menu-bg open-eos-player .v-sheet,
.julia-menu-bg open-eos-player .say,
.julia-menu-bg open-eos-player [class*="say"],
.julia-menu-bg open-eos-player [class*="message"] {
  background: rgba(10,10,10,.38) !important;
  backdrop-filter: blur(11px) !important;
  -webkit-backdrop-filter: blur(11px) !important;
  box-shadow: 0 6px 20px rgba(0,0,0,.22) !important;
}

/* Buttons also slightly translucent so the whole menu has one visual language. */
.julia-menu-bg .v-btn {
  background-color: rgba(20,20,20,.58) !important;
  backdrop-filter: blur(8px) !important;
  -webkit-backdrop-filter: blur(8px) !important;
}

/* Hide visual timer anywhere near the top-left or top-right.
   Timers continue running; only their DOM display is hidden. */
.oeos-timer, .eos-timer, .timer-display, .countdown-timer,
[class*="timer-display"], [class*="countdown-timer"],
[class*="timerDisplay"], [class*="countdownTimer"],
[class*="timer"][class*="display"],
[class*="countdown"] {
  display: none !important;
}


/* ===== JULIA FINAL TUNE - latest overrides ===== */

/* Intro: less empty floor below the heels, more legs visible above.
   cover keeps the image proportional; no stretching. */
.julia-prestart-bg {
  background-position: 50% 88% !important;
}

/* Keep menu focal point */
.julia-menu-bg {
  background-position: 40% 65% !important;
}

/* Move menu a touch further upward */
.julia-menu-bg open-eos-player {
  position: relative !important;
  top: -115px !important;
}

/* Make ALL menu backing layers transparent so the photo really shows through.
   The inline text panels provide the frosted glass effect. */
.julia-menu-bg open-eos-player,
.julia-menu-bg open-eos-player > *,
.julia-menu-bg open-eos-player .v-card,
.julia-menu-bg open-eos-player .v-sheet,
.julia-menu-bg open-eos-player [class*="say"],
.julia-menu-bg open-eos-player [class*="message"],
.julia-menu-bg open-eos-player [class*="container"] {
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* Buttons: translucent but readable */
.julia-menu-bg .v-btn {
  background-color: rgba(15,15,15,.42) !important;
  backdrop-filter: blur(6px) !important;
  -webkit-backdrop-filter: blur(6px) !important;
}

/* Hide timer display only; timer logic remains active. */
.oeos-timer, .eos-timer, .timer-display, .countdown-timer,
[class*="timer-display"], [class*="countdown-timer"],
[class*="timerDisplay"], [class*="countdownTimer"],
[class*="timer"][class*="display"],
[class*="countdown"] {
  display: none !important;
}


/* ===== JULIA MENU TRANSPARENCY + IMMEDIATE SLAP BUTTON PATCH ===== */

/* Remove black OEOS wrapper layers on the MENU only. */
.julia-menu-bg open-eos-player,
.julia-menu-bg open-eos-player > *,
.julia-menu-bg open-eos-player div,
.julia-menu-bg open-eos-player section,
.julia-menu-bg open-eos-player main,
.julia-menu-bg open-eos-player article {
  background-color: transparent !important;
}

/* The ONLY intended menu text backgrounds: very transparent frosted glass. */
.julia-menu-bg .julia-menu-panel {
  background: rgba(0,0,0,.15) !important;
  backdrop-filter: blur(5px) !important;
  -webkit-backdrop-filter: blur(5px) !important;
  box-shadow: 0 4px 16px rgba(0,0,0,.14) !important;
}

/* Keep buttons visible but translucent. */
.julia-menu-bg .v-btn,
.julia-menu-bg button {
  background: rgba(15,15,15,.34) !important;
  backdrop-filter: blur(5px) !important;
  -webkit-backdrop-filter: blur(5px) !important;
}


/* ===== REAL MENU BLACK CARD FIX =====
   OpenEOS SayBubble defaults every say card to color="black".
   This exact root card is what stayed black behind our transparent inner panel. */
.julia-menu-bg .oeos-say-bubble {
  background-color: rgba(0, 0, 0, 0.15) !important;
  background: rgba(0, 0, 0, 0.15) !important;
  backdrop-filter: blur(5px) !important;
  -webkit-backdrop-filter: blur(5px) !important;
  box-shadow: 0 4px 14px rgba(0,0,0,.12) !important;
}

/* Inner custom panel should not add a second dark layer. */
.julia-menu-bg .oeos-say-bubble .julia-menu-panel {
  background: transparent !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
}

/* Keep menu buttons translucent. */
.julia-menu-bg .v-btn,
.julia-menu-bg button {
  background: rgba(15,15,15,.34) !important;
}


/* ===== JULIA GET READY SCREEN =====
   Shown immediately after START, for the existing 5-second startgame page. */
.julia-getready-bg {
  background-image: url('/images/ui/getready.jpg') !important;
  background-color: #000 !important;
  background-size: cover !important;
  background-position: 50% 50% !important;
  background-repeat: no-repeat !important;
  min-height: 100vh !important;
}
.julia-getready-bg .v-app-bar { display: none !important; }
.julia-getready-bg .v-main { padding: 0 !important; min-height: 100vh !important; }
.julia-getready-bg .v-application--wrap,
.julia-getready-bg .v-main,
.julia-getready-bg .v-main__wrap,
.julia-getready-bg open-eos-player,
.julia-getready-bg open-eos-player > * {
  background: transparent !important;
  background-color: transparent !important;
}
/* Keep GET READY in its existing EOS position, but remove the solid black card. */
.julia-getready-bg .oeos-say-bubble {
  background: rgba(0,0,0,.15) !important;
  background-color: rgba(0,0,0,.15) !important;
  backdrop-filter: blur(5px) !important;
  -webkit-backdrop-filter: blur(5px) !important;
  box-shadow: 0 4px 14px rgba(0,0,0,.12) !important;
}


/* ===== JULIA WARNING PANELS =====
   Same transparent look as the rest: 15% black + 5px blur. */

/* I WANNA SUFFER warning: same page, no background/page switch. */
.julia-cbtwarning-bg .oeos-say-bubble {
  background: rgba(0,0,0,.15) !important;
  background-color: rgba(0,0,0,.15) !important;
  backdrop-filter: blur(5px) !important;
  -webkit-backdrop-filter: blur(5px) !important;
  box-shadow: 0 4px 14px rgba(0,0,0,.12) !important;
}
.julia-cbtwarning-bg .v-btn,
.julia-cbtwarning-bg button {
  background: rgba(0,0,0,.15) !important;
  background-color: rgba(0,0,0,.15) !important;
  backdrop-filter: blur(5px) !important;
  -webkit-backdrop-filter: blur(5px) !important;
}

/* START warning over the existing getready.jpg background. */
.julia-getready-bg .oeos-say-bubble {
  background: rgba(0,0,0,.15) !important;
  background-color: rgba(0,0,0,.15) !important;
  backdrop-filter: blur(5px) !important;
  -webkit-backdrop-filter: blur(5px) !important;
  box-shadow: 0 4px 14px rgba(0,0,0,.12) !important;
}
.julia-getready-bg .v-btn,
.julia-getready-bg button {
  background: rgba(0,0,0,.15) !important;
  background-color: rgba(0,0,0,.15) !important;
  backdrop-filter: blur(5px) !important;
  -webkit-backdrop-filter: blur(5px) !important;
}


/* ===== JULIA MOBILE + FULLSCREEN BACKGROUND FIX =====
   v-main is the element sent to browser fullscreen, so the backgrounds
   must also live on v-main, not only on the outer v-app. */

.v-main.julia-prestart-bg,
.v-main.julia-menu-bg,
.v-main.julia-getready-bg {
  background-color: #000 !important;
  background-repeat: no-repeat !important;
  background-size: cover !important;
  min-height: 100vh !important;
  min-height: 100dvh !important;
}

/* Intro / first image */
.v-main.julia-prestart-bg {
  background-image: url('/images/ui/welcome.jpg') !important;
  background-position: 50% 88% !important;
}

/* Menu + every settings screen before START */
.v-main.julia-menu-bg {
  background-image: url('/images/ui/menu.jpg') !important;
  background-position: 40% 65% !important;
}

/* Third image / get-ready screen */
.v-main.julia-getready-bg {
  background-image: url('/images/ui/getready.jpg') !important;
  background-position: center center !important;
}

/* In browser fullscreen force the active background to fill the fullscreen element. */
.v-main:fullscreen,
.v-main:-webkit-full-screen {
  width: 100vw !important;
  height: 100vh !important;
  min-height: 100vh !important;
  background-color: #000 !important;
}
.v-main.julia-prestart-bg:fullscreen,
.v-main.julia-menu-bg:fullscreen,
.v-main.julia-getready-bg:fullscreen,
.v-main.julia-prestart-bg:-webkit-full-screen,
.v-main.julia-menu-bg:-webkit-full-screen,
.v-main.julia-getready-bg:-webkit-full-screen {
  background-repeat: no-repeat !important;
  background-size: cover !important;
}

/* Stop OpenEOS/Vuetify inner layers from painting black over menu/settings backgrounds. */
.v-main.julia-menu-bg .v-main__wrap,
.v-main.julia-menu-bg open-eos-player,
.v-main.julia-menu-bg open-eos-player > *,
.v-main.julia-menu-bg [class*="player"],
.v-main.julia-menu-bg [class*="page"],
.v-main.julia-cbtwarning-bg .v-main__wrap,
.v-main.julia-cbtwarning-bg open-eos-player,
.v-main.julia-cbtwarning-bg open-eos-player > * {
  background-color: transparent !important;
  background: transparent !important;
}

/* Same frosted look as the existing Julia menu. */
.v-main.julia-menu-bg .oeos-say-bubble,
.v-main.julia-cbtwarning-bg .oeos-say-bubble,
.v-main.julia-getready-bg .oeos-say-bubble {
  background: rgba(0,0,0,.15) !important;
  background-color: rgba(0,0,0,.15) !important;
  backdrop-filter: blur(5px) !important;
  -webkit-backdrop-filter: blur(5px) !important;
}

.v-main.julia-menu-bg .v-btn,
.v-main.julia-cbtwarning-bg .v-btn,
.v-main.julia-getready-bg .v-btn {
  background: rgba(0,0,0,.15) !important;
  backdrop-filter: blur(5px) !important;
  -webkit-backdrop-filter: blur(5px) !important;
}


/* Julia video library. EOS choices remain responsible for navigation. */
.julia-video-thumbnails { display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;max-width:950px;margin:18px auto;padding:12px;position:relative;z-index:2; }
.julia-video-tile { color:white;text-align:center;font-weight:bold;text-shadow:0 2px 5px black;background:transparent;border:0;cursor:pointer;padding:0; }
.julia-video-tile:focus-visible { outline:3px solid white;outline-offset:3px;border-radius:10px; }
.julia-video-tile img { width:100%;aspect-ratio:16/9;object-fit:cover;border-radius:10px;border:2px solid rgba(255,255,255,.5);display:block;margin-bottom:7px; }
.julia-video-panel { position:relative;z-index:2;width:min(96%,960px);margin:16px auto;text-align:center;color:white;text-shadow:0 2px 5px black; }
.julia-video-panel video { width:100%;max-height:65vh;background:#000;border-radius:10px;display:block; }
.julia-video-panel p { margin:8px 0 0;font-weight:bold; }
@media(max-width:650px) { .julia-video-thumbnails { grid-template-columns:1fr;max-width:360px; } }

/* Fullscreen layout on every page, with no toolbar offset. */
.v-application .v-main {
  padding-top: 0 !important;
  min-height: 100vh !important;
}
.v-application .v-main__wrap {
  min-height: 100vh;
}
.julia-video-settings{position:relative;z-index:10;margin:12px auto;display:flex;flex-direction:column;align-items:center;gap:10px;color:#fff;text-shadow:0 2px 4px #000}
.julia-video-settings input{width:110px;text-align:center;background:rgba(0,0,0,.3);color:#fff;border:1px solid rgba(255,255,255,.6);border-radius:5px;padding:8px;font-size:18px}
</style>

<style>
.julia-test-overlay{position:fixed;inset:0;z-index:9000;background:rgba(0,0,0,.96);display:flex;align-items:center;justify-content:center;padding:14px;overflow:auto;color:#fff}
.julia-test-card{width:min(100%,620px);max-height:96vh;overflow:auto;text-align:center}
.julia-test-card video,.julia-test-card img{max-height:43vh;max-width:100%;object-fit:contain;border-radius:8px}
.julia-test-card h2{font-size:23px;margin:10px}.julia-test-question{font-size:18px;margin:12px 0}
.julia-test-card button{display:block;width:100%;margin:9px 0;padding:13px;border:1px solid #fff;border-radius:7px;color:#fff;background:#27212b;font-size:16px}
/* A video REPLACES the Open EOS slide picture; it must not sit on top of it. */
.julia-video-replaces-image img[src*="/images/feet/"],
.julia-video-replaces-image img[src*="/images/socks/"],
.julia-video-replaces-image [style*="background-image"][style*="/images/feet/"],
.julia-video-replaces-image [style*="background-image"][style*="/images/socks/"] { visibility:hidden !important; }
/* Video is a background layer; game UI stays in front on mobile too. */
.julia-normal-slide-video{position:fixed;z-index:0;left:50%;top:12%;transform:translateX(-50%);max-width:90vw;max-height:65vh;object-fit:contain;pointer-events:none}
.julia-video-replaces-image open-eos-player{position:relative;z-index:1}
.julia-video-replaces-image button,.julia-video-replaces-image .v-btn{position:relative;z-index:2}
</style>

<style>
.bm-layer { position: fixed; inset: 0; z-index: 0; pointer-events: none; overflow: hidden; background: #000; }
.bm-layer video, .bm-layer img { width: 100%; height: 100%; object-fit: cover; }
.v-application > .v-application--wrap { position: relative; z-index: 1; }
.bm-open { position: fixed; right: 12px; top: 12px; z-index: 10001; color: white; background: rgba(0,0,0,.7); border: 1px solid #aaa; border-radius: 7px; padding: 8px 11px; font: bold 11px Arial,sans-serif; }
.bm-shade { position: fixed; inset: 0; z-index: 10002; background: rgba(0,0,0,.8); display: flex; align-items: center; justify-content: center; padding: 10px; }
.bm-dialog { width: min(540px,100%); max-height: 94vh; overflow: auto; background: #1d1d1d; color: #fff; padding: 16px; border: 1px solid #888; border-radius: 12px; font: 14px Arial,sans-serif; }
.bm-dialog header,.bm-actions,.bm-tabs { display:flex; justify-content:space-between; gap:8px; margin-bottom:12px; }
.bm-dialog button { background:#444; color:#fff; border:1px solid #999; border-radius:6px; padding:9px; cursor:pointer; }
.bm-tabs button { flex:1; }.bm-tabs .selected { background:#8461a5; }
.bm-preview { height: min(30vh,230px); display:flex; justify-content:center; align-items:center; background:#050505; margin-bottom:12px; }
.bm-preview { overflow:hidden; }
.bm-preview img,.bm-preview video { width:100%; height:100%; object-fit:cover; }
.bm-field { display:block; margin:10px 0; font-weight:bold; }
.bm-field input,.bm-field select { display:block; box-sizing:border-box; width:100%; margin-top:5px; padding:8px; color:#fff; background:#333; border:1px solid #777; }
.bm-check { display:flex; gap:8px; align-items:center; margin:12px 0; }
.bm-note { color:#ddd; font-size:12px; line-height:1.5; }
.bm-actions { flex-wrap:wrap; }
</style>

<style>
/* Remove original fixed JPG only when a managed background is selected. */
.bm-custom-active.julia-prestart-bg, .bm-custom-active.julia-menu-bg,
.bm-custom-active.julia-cbtwarning-bg, .bm-custom-active.julia-getready-bg,
.bm-custom-active .v-main.julia-prestart-bg,
.bm-custom-active .v-main.julia-menu-bg,
.bm-custom-active .v-main.julia-cbtwarning-bg,
.bm-custom-active .v-main.julia-getready-bg { background-image: none !important; background-color: transparent !important; }
.bm-custom-active .v-application--wrap, .bm-custom-active .v-main, .bm-custom-active .v-main__wrap { background-color: transparent !important; }
</style>

<style scoped>
.bm-crop-controls { border:1px solid #666; border-radius:8px; padding:10px; margin:12px 0; }
.bm-crop-controls label { display:block; margin:9px 0; font-size:12px; }
.bm-crop-controls input[type="range"] { display:block; width:100%; }
.bm-crop-controls small { display:block; margin-top:7px; color:#ccc; }
</style>
